import React, { useEffect, useState } from 'react';
import { generateBrief, getPeers, type Peers } from '../../../api/governmentApi';
import type { GeneratedReport } from '../../../api/reportsApi';
import ReportViewer from '../../reports/ReportViewer';

/** SRS #58 — the policy desk on the Government dashboard: where this jurisdiction stands against peers, and a one-click policy brief from the live record. */
const LEVEL: Record<string, { bg: string; fg: string }> = { high: { bg: 'var(--ds-danger-bg)', fg: 'var(--ds-danger)' }, elevated: { bg: 'var(--ds-warning-bg)', fg: 'var(--ds-warning)' }, watch: { bg: 'var(--ds-info-bg)', fg: 'var(--ds-info)' }, quiet: { bg: 'var(--ds-surface-2)', fg: 'var(--ds-text-secondary)' } };
const S = {
  wrap: { border: '1px solid var(--ds-border)', borderRadius: 12, padding: 16, display: 'grid', gap: 12, background: 'var(--ds-surface)' } as React.CSSProperties,
  kicker: { fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ds-text-muted)', fontWeight: 600 } as React.CSSProperties,
  muted: { color: 'var(--ds-text-muted)', fontSize: 12.5 } as React.CSSProperties,
  th: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--ds-border)', color: 'var(--ds-text-muted)', fontSize: 11.5, textTransform: 'uppercase', whiteSpace: 'nowrap' } as React.CSSProperties,
  td: { padding: '6px 8px', borderBottom: '1px solid var(--ds-surface-2)', fontSize: 13 } as React.CSSProperties,
  primary: { padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--ds-brand-blue)', color: 'var(--ds-text-on-dark)', fontWeight: 700, cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
};

const PolicyDesk: React.FC = () => {
  const [peers, setPeers] = useState<Peers | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'locked' | 'unset' | 'error'>('loading');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [brief, setBrief] = useState<GeneratedReport | null>(null);
  useEffect(() => {
    getPeers().then(p => { setPeers(p); setState('ok'); }).catch((e: Error & { status?: number }) => {
      if (e?.status === 403) setState('locked'); else if (e?.status === 409) { setState('unset'); setMessage(e.message); } else { setState('error'); setMessage(e.message); }
    });
  }, []);
  const run = async () => {
    setBusy(true); setMessage(null);
    try { setBrief(await generateBrief({})); } catch (e) { setMessage(e instanceof Error ? e.message : 'The brief could not be generated'); } finally { setBusy(false); }
  };
  if (state === 'locked') return null;
  return (
    <section style={S.wrap} aria-label="Policy desk" data-testid="policy-desk">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <div>
          <div style={S.kicker}>Policy desk</div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{peers ? `Where ${peers.mine.join(', ')} stands` : 'Where your jurisdiction stands'}</div>
        </div>
        <span style={{ flex: 1 }} />
        {state === 'ok' && <button type="button" style={S.primary} onClick={run} disabled={busy}>{busy ? 'Writing the brief…' : 'Generate policy brief'}</button>}
      </div>
      {state === 'unset' && <div style={S.muted}>{message}</div>}
      {(state === 'error' || (message && state === 'ok')) && <div role="alert" style={{ color: 'var(--ds-danger-text)', fontSize: 13 }}>{message}</div>}
      {brief && <div data-testid="brief-ready"><ReportViewer report={brief} onClose={() => setBrief(null)} formatLabel="Policy brief" /></div>}
      {peers && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Jurisdiction', 'AI instruments', 'In force', 'Upcoming', 'Consultations open', 'Effective ≤ 90 d', 'Actions (90 d)', 'Early warning', 'Leading obligations'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {peers.rows.map(r => (
                <tr key={r.jurisdiction} data-testid={`peer-${r.jurisdiction}`} style={r.mine ? { background: 'var(--ds-info-bg)', fontWeight: 700 } : undefined}>
                  <td style={S.td}>{r.jurisdiction}{r.mine ? ' (you)' : ''} <span style={S.muted}>#{r.rank_in_force}</span></td>
                  <td style={S.td}>{r.instruments}</td><td style={S.td}>{r.in_force}</td><td style={S.td}>{r.upcoming}</td><td style={S.td}>{r.consultations_open}</td><td style={S.td}>{r.effective_90d}</td><td style={S.td}>{r.recent_actions_90d}</td>
                  <td style={S.td}><span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: 11.5, background: LEVEL[r.warning_level].bg, color: LEVEL[r.warning_level].fg }}>{r.warning_level}</span></td>
                  <td style={{ ...S.td, fontWeight: 400 }}>{r.top_obligations.map(o => o.replace(/_/g, ' ')).join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ ...S.muted, marginTop: 6 }}>Counts of AI-relevant instruments only; ranked by instruments in force. Compared with {peers.compared_with.join(', ') || 'no peers with data yet'}. The brief uses the {peers.mine[0]} record only.</div>
        </div>
      )}
    </section>
  );
};

export default PolicyDesk;
